import { AuthLayout } from '@avanta/shared/components/AuthLayout';
import { UserType } from '@avanta/shared/constants/users';
import { ALL_USER_TYPES } from '@avanta/shared/types/users';
import { Navigate } from 'react-router';
import { Layout } from 'components/Layout';
import { ROUTES } from 'constants/routes';
import { AppointmentsCheckout } from 'pages/Appointments/AppointmentsCheckout';
import { AppointmentsList } from 'pages/Appointments/AppointmentsList';
import { AppointmentsPersonalData } from 'pages/Appointments/AppointmentsPersonalData';
import { AppointmentsReserve } from 'pages/Appointments/AppointmentsReserve';
import { Login } from 'pages/Auth/Login';
import { MagicLink } from 'pages/Auth/MagicLink';
import { RecoverPassword } from 'pages/Auth/RecoverPassword';
import { Signup } from 'pages/Auth/Signup';
import { Chat } from 'pages/Chat';
import { ChatReport } from 'pages/ChatReport';
import { Home } from 'pages/Home';
import { Pae } from 'pages/Pae';
import { Profile } from 'pages/Profile';
import {
  PatientProfile,
  PsychologistAppointment,
  PsychologistPatientsList,
} from 'pages/Psychologist';
import { Sapa } from 'pages/Sapa';
import { SapaChat } from 'pages/SapaChat';
import { WellBeing } from 'pages/WellBeing';
import { WellBeingBarometer } from 'pages/WellBeingBarometer';
import { WellBeingContent } from 'pages/WellBeingContent';
import { ArticlePage } from 'pages/WellBeingContent/components/ArticlePage';

type Path = {
  path: string;
  component: React.ReactElement;
  roles?: UserType[];
  layout?: React.ReactElement;
};

const APPOINTMENTS_ROUTES = [
  {
    path: ROUTES.APPOINTMENTS,
    component: <AppointmentsList />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
  {
    path: ROUTES.APPOINTMENTS_PERSONAL_DATA,
    component: <AppointmentsPersonalData />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
  {
    path: ROUTES.APPOINTMENTS_RESERVE,
    component: <AppointmentsReserve />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE, UserType.ENTERPRISE, UserType.PSYCHOLOGIST],
  },
  {
    path: ROUTES.APPOINTMENTS_CHECKOUT,
    component: <AppointmentsCheckout />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
  },
];

const WELL_BEING_ROUTES = [
  {
    path: ROUTES.WELL_BEING,
    component: <WellBeing />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
  {
    path: ROUTES.WELL_BEING_CONTENT,
    component: <WellBeingContent />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
  {
    path: ROUTES.WELL_BEING_ARTICLE,
    component: <ArticlePage />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
  {
    path: ROUTES.WELL_BEING_TEST,
    component: <WellBeingContent />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
  {
    path: ROUTES.WELL_BEING_TEST_DETAIL,
    component: <WellBeingContent />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
  {
    path: ROUTES.WELL_BEING_BAROMETER,
    component: <WellBeingBarometer />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
];

const CHAT_ROUTES = [
  {
    path: ROUTES.CHAT,
    component: <Chat />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE, UserType.SUPPORTER, UserType.PSYCHOLOGIST],
  },
  {
    path: ROUTES.CHAT_REPORT,
    component: <ChatReport />,
    roles: [UserType.SUPPORTER, UserType.PSYCHOLOGIST],
  },
];

const PSYCHOLOGIST_ROUTES = [
  {
    path: ROUTES.PSYCHOLOGIST_PATIENTS,
    component: <PsychologistPatientsList />,
    roles: [UserType.PSYCHOLOGIST],
    layout: <Layout />,
  },
  {
    path: ROUTES.PATIENT_PROFILE,
    component: <PatientProfile />,
    roles: [UserType.PSYCHOLOGIST],
    layout: <Layout />,
  },
  {
    path: ROUTES.PATIENT_APPOINTMENTS,
    component: <PsychologistAppointment />,
    roles: [UserType.PSYCHOLOGIST],
    layout: <Layout />,
  },
];

export const PRIVATE_PATHS: Path[] = [
  {
    path: ROUTES.HOME,
    component: <Home />,
    roles: ALL_USER_TYPES,
    layout: <Layout />,
  },
  {
    path: ROUTES.PAE,
    component: <Pae />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
  {
    path: ROUTES.SAPA,
    component: <Sapa />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
  {
    path: ROUTES.PROFILE,
    component: <Profile />,
    roles: ALL_USER_TYPES,
    layout: <Layout />,
  },
  {
    path: ROUTES.PROFILE_SETUP,
    component: <Signup />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
  },
  {
    path: ROUTES.SAPA_CHAT,
    component: <SapaChat />,
    roles: [UserType.PATIENT, UserType.EMPLOYEE],
    layout: <Layout />,
  },
  ...APPOINTMENTS_ROUTES,
  ...CHAT_ROUTES,
  ...WELL_BEING_ROUTES,
  ...PSYCHOLOGIST_ROUTES,
];

export const PUBLIC_ROUTES: Path[] = [
  {
    path: ROUTES.LOGIN,
    component: <Login />,
    layout: <AuthLayout />,
  },
  {
    path: ROUTES.MAGIC_LINK,
    component: <MagicLink />,
    layout: <AuthLayout />,
  },
  {
    path: ROUTES.RECOVER_PASSWORD,
    component: <RecoverPassword />,
    layout: <AuthLayout />,
  },
  {
    path: ROUTES.REGISTER,
    component: <Signup />,
    layout: <AuthLayout />,
  },
];

export const FALLBACK_ROUTE: Path = {
  path: '*',
  component: <Navigate to={ROUTES.HOME} />,
};

export const ROOT = {
  path: ROUTES.ROOT,
  component: <Home />,
  roles: ALL_USER_TYPES,
};
